<script lang="ts" setup>
import { RouteRecordRaw, useRouter } from "vue-router";

import { computed } from "vue";
import { SysOrgRoleName } from "~/api";
import { applicationRouteMap } from "~/router/mainRouter";
import HelpAndSupportMenuSection from "~/components/HelpAndSupportMenuSection.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";

const router = useRouter();
const myInfoStore = useMyInfoStore();
myInfoStore.load();

/** Starting section of the route we are currently navigated to. Ex. "/forms" */
const currentRouteStart = computed<string>(
  () => "/" + router.currentRoute.value.path.split("/")[1],
);

const filterRoutes = (r: RouteRecordRaw) => {
  if (r["meta"]) {
    const meta = r.meta;
    if ("superAdmin" in meta) {
      if (meta.superAdmin && myInfoStore.userInfo.superAdmin) {
        return true;
      }
    }
    const roles = meta.roles as SysOrgRoleName[];
    if (roles?.length) {
      return roles?.some((role: SysOrgRoleName) =>
        myInfoStore.currentSysOrg?.roles.includes(role),
      );
    }
    return false;
  }

  return true;
};

const routes = applicationRouteMap.Compliance.children;

const manageWorkflowRoutes = computed(() =>
  Object.values(routes.ManageWorkflows.children)
    .filter(filterRoutes)
    .map((r: RouteRecordRaw) => {
      if (r.children) {
        r.children = Object.values(r.children).filter(filterRoutes);
      }
      return r;
    }),
);

const manageRoutes = computed(() =>
  Object.values(routes.Manage.children)
    .filter(filterRoutes)
    .map((r: RouteRecordRaw) => {
      if (r.children) {
        r.children = Object.values(r.children).filter(filterRoutes);
      }
      return r;
    }),
);

const setupRoutes = computed(() =>
  Object.values(routes.Setup.children).filter(filterRoutes),
);

const dashboardRoutes = computed(() =>
  Object.values(routes.Dashboard.children).filter(filterRoutes),
);

const workflowRoutes = computed(() =>
  Object.values(routes.Workflows.children).filter(filterRoutes),
);

const adminRoutes = computed(() =>
  Object.values(routes.Admin.children).filter(filterRoutes),
);

const emailRoutes = computed(() =>
  Object.values(routes.Email.children).filter(filterRoutes),
);

const techRoutes = computed(() =>
  Object.values(routes.TechSupport.children).filter(filterRoutes),
);

const crmRoutes = computed(() =>
  Object.values(routes.CRM.children).filter(filterRoutes),
);

const hasSomeRoutes = computed(
  () =>
    setupRoutes.value.length ||
    dashboardRoutes.value.length ||
    workflowRoutes.value.length ||
    adminRoutes.value.length ||
    manageRoutes.value.length ||
    manageWorkflowRoutes.value.length ||
    crmRoutes.value.length ||
    techRoutes.value.length,
);

const isCurrentRoute = (path: string): boolean =>
  currentRouteStart.value === "/"
    ? false
    : path.startsWith(currentRouteStart.value);
</script>

<template>
  <div v-if="hasSomeRoutes || myInfoStore.isLoggedIn" class="sidebar">
    <!-- Show the sidebar for any logged-in user so Help and Support (below) stays
    available even when no other route groups are permitted. -->
    <div v-if="dashboardRoutes.length" class="section">
      <div class="header">Dashboards</div>
      <router-link
        v-for="route in dashboardRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.Compliance.path}${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.meta.display }}
      </router-link>
    </div>

    <div v-if="manageWorkflowRoutes.length" class="section setup-start">
      <div class="header">Workflows</div>
      <template v-for="route in manageWorkflowRoutes" :key="route.path">
        <router-link
          :to="`${applicationRouteMap.Compliance.path}${route.path}`"
          :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
          class="routes"
        >
          {{ route.meta?.display }}
        </router-link>
        <div v-if="route.children?.length" class="subSection">
          <router-link
            v-for="childRoute in Object.values(route.children).filter(
              filterRoutes,
            )"
            :key="childRoute.path"
            :to="`${applicationRouteMap.Compliance.path}${childRoute.path}`"
            :class="{
              'router-link-exact-active': isCurrentRoute(childRoute.path),
            }"
            class="routes"
          >
            {{ childRoute.meta?.display }}
          </router-link>
        </div>
      </template>
    </div>

    <div v-if="crmRoutes.length" class="section setup-start">
      <div class="header">CRM</div>
      <router-link
        v-for="route in crmRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.Compliance.path}${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.meta?.display }}
      </router-link>
    </div>

    <div v-if="manageRoutes.length" class="section setup-start">
      <div class="header">Entities</div>
      <template v-for="route in manageRoutes" :key="route.path">
        <router-link
          :to="`${applicationRouteMap.Compliance.path}${route.path}`"
          :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
          class="routes"
        >
          {{ route.meta?.display }}
        </router-link>
        <div v-if="route.children?.length" class="subSection">
          <router-link
            v-for="childRoute in Object.values(route.children).filter(
              filterRoutes,
            )"
            :key="childRoute.path"
            :to="`${applicationRouteMap.Compliance.path}${childRoute.path}`"
            :class="{
              'router-link-exact-active': isCurrentRoute(childRoute.path),
            }"
            class="routes"
          >
            {{ childRoute.meta?.display }}
          </router-link>
        </div>
      </template>
    </div>

    <div v-if="setupRoutes.length" class="section">
      <div class="header">Setup</div>
      <router-link
        v-for="route in setupRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.Compliance.path}${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.meta.display }}
      </router-link>
    </div>

    <div v-if="emailRoutes.length" class="section">
      <div class="header">Email</div>
      <router-link
        v-for="route in emailRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.Compliance.path}${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.meta.display }}
      </router-link>
    </div>

    <div v-if="adminRoutes.length" class="section setup-end">
      <div class="header">Admin</div>
      <router-link
        v-for="route in adminRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.Compliance.path}${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.meta.display }}
      </router-link>
    </div>

    <div v-if="techRoutes.length" class="section">
      <div class="header">Tech Support</div>
      <router-link
        v-for="route in techRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.Compliance.path}${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.meta.display }}
      </router-link>
    </div>

    <HelpAndSupportMenuSection
      :support-tickets-path="`${applicationRouteMap.Compliance.path}support-tickets`"
      :help-faq-path="`${applicationRouteMap.Compliance.path}help-faq`"
      :is-current-route="isCurrentRoute"
    />

    <el-button
      v-if="myInfoStore.isSuperAdmin"
      type="primary"
      size="small"
      @click="router.push('/portal/')"
    >
      SA &rarr; Housing Portal Menus
    </el-button>
  </div>
</template>

<style lang="less">
.sidebar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: min-content;
  overflow-y: auto;
  padding: 1em 1.5em 3em 0.5em;
  font-size: 1.1rem;
  background-color: var(--el-color-primary-dark-3);

  .section,
  .subSection {
    > a {
      text-wrap: nowrap;
      color: var(--el-color-white);
      text-decoration: none;
      padding-left: 0.8rem;
    }
    > a.router-link-exact-active {
      color: #ffd04b;
    }
    .header {
      font-weight: 600;
      color: var(--el-color-primary-light-8);
      cursor: default;
      margin-top: 1em;
    }

    .routes {
      display: flex;
      flex-direction: column;
      padding-left: 0.8rem;
    }

    &:has(.router-link-exact-active) {
      .header {
        color: #ffd04b;
      }
    }
  }
  .subSection {
    padding-left: 0.5em;
    .routes {
      padding-left: 0.8rem;
    }
  }

  .setup-start,
  .setup-end {
    border-top: 1px solid var(--el-color-primary-light-8);
    margin-top: 1em;
  }
}
</style>
